import { Plus } from "lucide-react-native";
import { memo } from "react";

import ThreadListContent from "@/features/knowledge-graph/components/molecules/ThreadListContent";

import { Badge, BadgeText } from "@/components/atoms/Badge";
import type { ListRenderItem } from "@/components/atoms/FlashList";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import type { UserThread } from "@/generated/graphql";

/**
 * Props for ThreadSidebarView component
 */
interface ThreadSidebarViewProps {
  isVisible: boolean;
  onClose: () => void;
  threads: UserThread[];
  keyExtractor: (item: UserThread) => string;
  renderItem: ListRenderItem<UserThread>;
  onNewThread: () => void;
  isLoading: boolean;
  errorMessage: string | null;
  emptyStateMessage: string | null;
  isDesktop: boolean;
}

/**
 * Computes the container class name based on desktop/mobile and visibility state
 * @param isDesktop - Whether the view is desktop
 * @param isVisible - Whether the sidebar is visible (mobile only)
 * @returns The computed class name for the container
 */
const getContainerClassName = (isDesktop: boolean, isVisible: boolean) =>
  isDesktop
    ? "h-full w-80 shrink-0 border-r border-outline-default bg-surface-base"
    : `absolute inset-y-0 left-0 z-40 w-72 border-r border-outline-default bg-surface-base shadow-hard-2 transition-transform duration-300 ease-in-out md:w-80 ${
        isVisible ? "translate-x-0" : "-translate-x-full"
      }`;

/**
 * Thread sidebar view component displaying conversation history
 * On desktop: always visible, inline in flex layout
 * On mobile: absolute positioned overlay that slides in/out
 * @param root0 - Component props
 * @param root0.isVisible - Whether sidebar is visible (mobile only)
 * @param root0.onClose - Handler to close sidebar
 * @param root0.threads - List of user threads
 * @param root0.keyExtractor - Pre-memoized key extractor function
 * @param root0.renderItem - Pre-memoized render item function
 * @param root0.onNewThread - Handler for creating new thread
 * @param root0.isLoading - Whether threads are loading
 * @param root0.errorMessage - Error message to display
 * @param root0.emptyStateMessage - Empty state message
 * @param root0.isDesktop - Whether view is desktop
 */
const ThreadSidebarView = ({
  isVisible,
  onClose: _onClose,
  threads,
  keyExtractor,
  renderItem,
  onNewThread,
  isLoading,
  errorMessage,
  emptyStateMessage,
  isDesktop,
}: ThreadSidebarViewProps) => (
  <Box className={getContainerClassName(isDesktop, isVisible)}>
    {/* Sidebar Header */}
    <Box className="flex h-14 flex-col justify-center border-b border-outline-default px-4 md:h-16 md:px-6">
      <Stack direction="horizontal" className="items-center gap-2">
        <Heading variant="title" className="text-content-primary md:text-title">
          Conversations
        </Heading>
        <Badge action="warning" size="sm" variant="solid">
          <BadgeText>EXPERIMENTAL</BadgeText>
        </Badge>
      </Stack>
    </Box>

    {/* New Thread Button */}
    <Box className="p-3 md:p-4">
      <Button
        variant="solid"
        size="sm"
        onPress={onNewThread}
        label="New Chat"
        labelVariant="body-strong"
        icon={Plus}
        iconSize="sm"
        className="w-full gap-1 rounded-sm bg-accent-primary shadow-sm"
      />
    </Box>

    {/* Thread List */}
    <ThreadListContent
      isLoading={isLoading}
      errorMessage={errorMessage}
      emptyStateMessage={emptyStateMessage}
      threads={threads}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
    />
  </Box>
);

ThreadSidebarView.displayName = "ThreadSidebarView";

export default memo(ThreadSidebarView);
